/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Hyunsu Sim (hssim@rsupport.com)
 *
 * @description
 *
 * @created at Wed Nov 13 2024
 **/

import { forwardRef, useEffect } from "react";
import { ReactQuillProps } from "@rsupport/react-quill";
import styled from "@emotion/styled";
import "quill/dist/quill.snow.css";

import { Maybe } from "@/frameworks/libraries/components";
import { Row } from "@/frameworks/designStuff/components";
import { ErrorBoundary } from "../ErrorBoundary";
import { MessageList } from "@/frameworks/core/chat";
import Vote from "./private/components/RenderItem/Vote";
import TransparentVote from "./private/components/RenderItem/TransparentVote";

export type VoteType = "create" | "end" | "remind";

interface ChatVoteRendererProps extends ReactQuillProps {
	startLine?: number;
	maxLine?: number;
	chatForm: ChatServices.Format2.VoteMessage;
	className?: string;
	onClick?: (item: ChatServices.Format2.Elements.AllVoteElements) => void;
	emergency?: boolean;
	vote: MessageList[string]["vote"];
	roomID: string;
	spaceID?: string;
	voteType: VoteType;
	variant?: ChatUI.Size;
	isSearch?: boolean;
}

function ChatRendererErrorFallback({ chatForm }: { chatForm?: ChatServices.Format2.VoteMessage }) {
	useEffect(() => {
		console.warn("#ChatRenderer# 채팅 메시지 구조 에러", chatForm);
	}, []);
	return null;
}

const VoteRenderer = forwardRef<HTMLDivElement, ChatVoteRendererProps>(
	({ chatForm, className, vote, roomID, spaceID, variant, isSearch = false }, ref) => {
		return (
			<ErrorBoundary fallback={<ChatRendererErrorFallback chatForm={chatForm} />}>
				<StyledRenderer ref={ref} className={`w-100 h-full ${className ?? ""}`} id={vote?.voteId}>
					<Maybe
						test={variant === "transparent"}
						fallback={
							<Vote
								vote={vote}
								roomID={roomID}
								spaceID={spaceID}
								voteType={chatForm.elements?.[0]?.key}
								isSearch={isSearch}
							/>
						}
					>
						<TransparentVote
							vote={vote}
							roomID={roomID}
							spaceID={spaceID}
							voteType={chatForm.elements?.[0]?.key}
						/>
					</Maybe>
				</StyledRenderer>
			</ErrorBoundary>
		);
	}
);

export default VoteRenderer;

const StyledRenderer = styled(Row)`
	position: relative;
	width: -webkit-fill-available;
	word-break: break-all;
	word-wrap: break-word;
`;
